import React, { useRef } from 'react';
import { 
  Shield, Clock, BarChart, 
  CheckCircle, ArrowRight, Wrench, FileCheck, 
  AlertCircle, Database, Code, Search, Activity, Settings
} from 'lucide-react';
import Button from '@/components/ui/Button';
import { COMPANY_INFO } from '@/lib/constants';
import { motion, useScroll, useTransform } from 'framer-motion';
import FunctionalityCard from '@/components/sections/FunctionalityCard';
import SEO from '@/components/SEO';
import BookingModule from '@/components/sections/BookingModule';
import EstimationForm from '@/components/sections/EstimationForm';
import FAQ from '@/components/ui/FAQ';

const MaintenanceSupport = () => {
  const servicesRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: servicesRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.8, 1, 1, 0.8]);

  const benefits = [
    {
      icon: <Shield className="w-full h-full text-orcsys-500" />,
      title: "Expertise certifiée",
      description: "Notre chef de projet certifié ISTQB garantit une approche professionnelle et méthodique de la maintenance."
    },
    {
      icon: <Clock className="w-full h-full text-orcsys-500" />,
      title: "Support réactif",
      description: "Une équipe dédiée pour répondre rapidement à vos besoins avec des temps d'intervention garantis."
    },
    {
      icon: <Wrench className="w-full h-full text-orcsys-500" />,
      title: "Maintenance préventive",
      description: "Anticipation et prévention des problèmes potentiels pour minimiser les interruptions de service."
    },
    {
      icon: <BarChart className="w-full h-full text-orcsys-500" />,
      title: "Amélioration continue",
      description: "Optimisation constante des performances et mise à jour régulière des fonctionnalités."
    }
  ];

  const maintenanceServices = [
    {
      icon: <Search className="w-5 h-5 text-orcsys-500" />,
      title: "Audit technique",
      description: "Évaluation approfondie de la qualité du code et de l'architecture"
    },
    {
      icon: <Shield className="w-5 h-5 text-orcsys-500" />,
      title: "Sécurité",
      description: "Mises à jour de sécurité et correction des vulnérabilités"
    },
    {
      icon: <Activity className="w-5 h-5 text-orcsys-500" />,
      title: "Monitoring",
      description: "Surveillance 24/7 des performances et de la disponibilité"
    },
    {
      icon: <Database className="w-5 h-5 text-orcsys-500" />,
      title: "Backup",
      description: "Sauvegarde régulière et plan de reprise d'activité"
    },
    {
      icon: <Code className="w-5 h-5 text-orcsys-500" />,
      title: "Évolution",
      description: "Développement de nouvelles fonctionnalités"
    },
    {
      icon: <Settings className="w-5 h-5 text-orcsys-500" />,
      title: "Optimisation",
      description: "Amélioration continue des performances"
    }
  ];

  const faqItems = [
    {
      question: "Que comprend votre service de maintenance ?",
      answer: "Notre service de maintenance est complet et inclut : la surveillance proactive des systèmes, les mises à jour de sécurité, l'optimisation des performances, la résolution des bugs, les sauvegardes régulières, et l'assistance technique. Nous assurons également la mise à jour des dépendances et la compatibilité avec les dernières versions des navigateurs."
    },
    {
      question: "Quels sont vos délais d'intervention en cas de problème ?",
      answer: "Nos délais d'intervention varient selon la criticité du problème : immédiat pour les incidents critiques, sous 4h pour les problèmes majeurs, et sous 24h pour les demandes standard. Notre équipe est disponible 24/7 pour les situations d'urgence, avec un système d'astreinte dédié."
    },
    {
      question: "Qu'est-ce que la certification ISTQB ?",
      answer: "La certification ISTQB est une certification internationale qui valide les compétences en test de logiciels. Nous sommes certifiés pour garantir la qualité de nos prestations."
    },
    {
      question: "Proposez-vous des rapports de performance ?",
      answer: "Oui, nous fournissons des rapports détaillés mensuels incluant : les métriques de performance, le temps de disponibilité, les incidents résolus, les mises à jour effectuées, et les recommandations d'amélioration. Ces rapports sont accessibles via notre plateforme de suivi."
    },
    {
      question: "Comment sont gérées les sauvegardes ?",
      answer: "Nous effectuons des sauvegardes automatiques quotidiennes, avec une rétention de 30 jours. Les données sont stockées de manière sécurisée sur plusieurs serveurs géographiquement distribués. Nous testons régulièrement les procédures de restauration pour garantir leur efficacité."
    },
    {
      question: "Peut-on faire évoluer l'application pendant la maintenance ?",
      answer: "Absolument ! Nous proposons un service d'évolution continue qui permet d'ajouter de nouvelles fonctionnalités ou d'optimiser les existantes. Chaque évolution est planifiée, testée et déployée de manière à minimiser l'impact sur l'utilisation de l'application."
    }
  ];

  return (
    <>
      <SEO 
        title="Maintenance & Support Technique | Services Professionnels Orcsys"
        description="Services de maintenance et support technique professionnels par Orcsys. Maintenance préventive et corrective, support réactif 24/7, monitoring continu et amélioration continue de vos applications. Équipe certifiée ISTQB."
        keywords="maintenance applicative, support technique, ISTQB, audit technique, maintenance préventive, maintenance corrective, monitoring 24/7, sécurité applicative, optimisation performance, sauvegarde données"
        type="service"
        url="https://innov.orcsys.fr/maintenance-support"
        image="https://innov.orcsys.fr/images/maintenance-support-preview.jpg"
      />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center pt-24 pb-16 overflow-hidden">
          {/* Background elements */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-white" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(142,227,205,0.3)_0%,rgba(255,255,255,0)_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(155,135,245,0.2)_0%,rgba(255,255,255,0)_60%)]" />
          
          <div className="container px-4 mx-auto relative z-10">
            <div className="max-w-5xl mx-auto">
              <motion.div 
                className="text-center mb-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                  Maintenance et Support <span className="text-orcsys-500">Professionnel</span>
                </h1>
                <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8">
                  Une équipe de professionnels pour maintenir, optimiser et faire évoluer 
                  vos applications avec une approche préventive et corrective.
                </p>
                
                <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                  <Button.Link 
                    href="#book" 
                    size="lg" 
                    className="bg-orcsys-500 hover:bg-orcsys-600 text-white group"
                  >
                    Demander un audit
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button.Link>
                  <Button.Link 
                    href="#services" 
                    variant="outline" 
                    size="lg"
                    className="border-orcsys-400 text-orcsys-500"
                  >
                    Découvrir nos services
                  </Button.Link>
                </div>
              </motion.div>

              {/* Statistics */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-3xl mx-auto mb-12"
              >
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-slate-100 shadow-sm">
                  <div className="text-3xl font-bold text-orcsys-500 mb-1">99.9%</div>
                  <div className="text-slate-700 text-sm">Disponibilité</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-slate-100 shadow-sm">
                  <div className="text-3xl font-bold text-orcsys-500 mb-1">24/7</div>
                  <div className="text-slate-700 text-sm">Support</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-slate-100 shadow-sm">
                  <div className="text-3xl font-bold text-orcsys-500 mb-1">&lt;2h</div>
                  <div className="text-slate-700 text-sm">Temps de réponse</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-slate-100 shadow-sm">
                  <div className="text-3xl font-bold text-orcsys-500 mb-1">ISTQB</div>
                  <div className="text-slate-700 text-sm">Certifié</div>
                </div>
              </motion.div>

              {/* Technical Environments & Tools */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="relative mx-auto max-w-4xl"
              >
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Environments */}
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 border border-slate-100 shadow-lg">
                    <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                      <Code className="w-6 h-6 text-orcsys-500 mr-3" />
                      Environnements techniques
                    </h3>
                    <ul className="space-y-4">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-orcsys-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Applications Web</span>
                          <p className="text-sm text-slate-600 mt-1">React, Node.js, PHP</p>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-orcsys-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Applications Mobiles</span>
                          <p className="text-sm text-slate-600 mt-1">Flutter, React Native, iOS (Swift), Android (Kotlin)</p>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-orcsys-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Bases de données</span>
                          <p className="text-sm text-slate-600 mt-1">Amazon RDS, MongoDB Atlas, PostgreSQL</p>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-orcsys-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Cloud & Infrastructure</span>
                          <p className="text-sm text-slate-600 mt-1">AWS, Docker, Kubernetes</p>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Tools */}
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 border border-slate-100 shadow-lg">
                    <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                      <Settings className="w-6 h-6 text-orcsys-500 mr-3" />
                      Outils de maintenance
                    </h3>
                    <ul className="space-y-4">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-orcsys-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Monitoring & Sécurité</span>
                          <p className="text-sm text-slate-600 mt-1">CloudWatch, GuardDuty, WAF, Shield</p>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-orcsys-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Déploiement & CI/CD</span>
                          <p className="text-sm text-slate-600 mt-1">CodePipeline, CodeBuild, CodeDeploy</p>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-orcsys-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Conteneurisation</span>
                          <p className="text-sm text-slate-600 mt-1">ECS, ECR, EKS</p>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-orcsys-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Serverless & API</span>
                          <p className="text-sm text-slate-600 mt-1">Lambda, API Gateway, DynamoDB</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* CTA - Contact */}
              <motion.div 
                className="mt-16 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <div className="inline-flex items-center text-slate-600 mb-2">
                  <AlertCircle className="w-4 h-4 text-orcsys-500 mr-2" />
                  <span>Besoin d'une intervention urgente ?</span>
                </div>
                <div className="flex justify-center">
                  <a 
                    href={`tel:${COMPANY_INFO.phone}`}
                    className="text-xl font-bold text-orcsys-500 hover:text-orcsys-600 transition-colors"
                  >
                    {COMPANY_INFO.phone}
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits section */}
        <section className="bg-white py-24">
          <div className="container px-4 mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Pourquoi choisir <span className="text-orcsys-500">Orcsys</span> ?
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                Une expertise reconnue en maintenance applicative et support technique,
                avec une approche certifiée et des processus éprouvés.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:border-orcsys-100 transition-all duration-300 hover:shadow-xl"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true, margin: "-100px" }}
                >
                  <div className="w-14 h-14 rounded-lg bg-orcsys-50 flex items-center justify-center mb-6 p-3">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services section */}
        <section ref={servicesRef} id="services" className="bg-slate-50 py-24">
          <div className="container px-4 mx-auto">
            <motion.div 
              className="max-w-5xl mx-auto"
              style={{ opacity, scale }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-6">
                    Maintenance <span className="text-orcsys-500">préventive et corrective</span>
                  </h2>
                  <p className="text-lg text-slate-600 mb-8">
                    Nous proposons une approche complète de la maintenance, adaptée aussi bien à nos 
                    produits qu'aux applications existantes après audit.
                  </p>

                  <ul className="space-y-4">
                    {[
                      "Audit technique complet avant prise en charge",
                      "Maintenance préventive planifiée",
                      "Intervention rapide en cas d'incident",
                      "Monitoring et alerting 24/7",
                      "Mises à jour de sécurité régulières",
                      "Évolution fonctionnelle sur demande"
                    ].map((item, index) => (
                      <motion.li 
                        key={index}
                        className="flex items-start"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <CheckCircle className="w-5 h-5 text-orcsys-500 mt-0.5 mr-3 flex-shrink-0" />
                        <span className="text-slate-700">{item}</span>
                      </motion.li>
                    ))}
                  </ul>

                  <div className="mt-8">
                    <Button.Link 
                      href="#estimation" 
                      variant="outline"
                      className="border-orcsys-400 text-orcsys-500"
                    >
                      Demander un devis
                    </Button.Link>
                  </div>
                </div>

                <div>
                  <div className="relative p-1 bg-white rounded-2xl shadow-xl border border-slate-200">
                    <div className="absolute inset-0 bg-gradient-to-br from-orcsys-50/30 to-transparent rounded-2xl" />
                    
                    <div className="relative grid grid-cols-2 gap-4 p-6">
                      {maintenanceServices.map((service, index) => (
                        <motion.div
                          key={index}
                          className="group"
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          viewport={{ once: true }}
                        >
                          <FunctionalityCard 
                            icon={service.icon} 
                            title={service.title} 
                            description={service.description}
                            index={index} 
                          />
                        </motion.div>
                      ))}
                    </div>

                    {/* Blob backgrounds */}
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-orcsys-500/10 rounded-full blur-3xl" />
                    <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-orcsys-500/10 rounded-full blur-3xl" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Process section */}
        <section className="bg-white py-24">
          <div className="container px-4 mx-auto">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Notre <span className="text-orcsys-500">processus</span> d'intervention
                </h2>
                <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                  Une méthodologie éprouvée pour garantir la qualité et l'efficacité 
                  de nos interventions de maintenance.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: <Search className="w-8 h-8 text-orcsys-500" />,
                    title: "1. Audit initial",
                    description: "Analyse approfondie du code, de l'architecture et des performances pour établir un plan d'action."
                  },
                  {
                    icon: <Wrench className="w-8 h-8 text-orcsys-500" />,
                    title: "2. Plan de maintenance",
                    description: "Définition des actions préventives et correctives, avec un calendrier d'intervention."
                  },
                  {
                    icon: <Activity className="w-8 h-8 text-orcsys-500" />,
                    title: "3. Suivi continu",
                    description: "Monitoring permanent, interventions planifiées et reporting régulier."
                  }
                ].map((step, index) => (
                  <motion.div
                    key={index}
                    className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:border-orcsys-100 transition-all duration-300 hover:shadow-xl"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-16 h-16 rounded-lg bg-orcsys-50 flex items-center justify-center mb-6">
                      {step.icon}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                    <p className="text-slate-600">{step.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-br from-orcsys-500 to-orcsys-600 py-20 relative overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBzdHJva2U9IiNmZmYiIHN0cm9rZS13aWR0aD0iMS41IiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiIHN0cm9rZS1vcGFjaXR5PSIwLjEiPjxjaXJjbGUgY3g9IjMwIiBjeT0iMzAiIHI9IjE1Ii8+PHBhdGggZD0iTTMwIDE1TDQ1IDMwIDMwIDQ1IDE1IDMweiIvPjwvZz48L3N2Zz4=')] opacity-30" />
          
          <div className="container px-4 mx-auto relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Besoin d'un support professionnel pour votre application ?
              </h2>
              <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
                Contactez-nous dès aujourd'hui pour discuter de vos besoins en maintenance 
                et découvrir comment nous pouvons vous aider à optimiser votre application.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button.Link 
                  href="#book" 
                  className="bg-white text-orcsys-600 hover:bg-white/90"
                  size="lg"
                >
                  Demander un audit
                </Button.Link>
                <Button.Link 
                  href={`tel:${COMPANY_INFO.phone}`}
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  size="lg"
                >
                  Nous appeler directement
                </Button.Link>
              </div>
            </div>
          </div>
        </section>

        <EstimationForm />
        
        <BookingModule projectType="maintenance" />

        <FAQ 
          title="Questions Fréquentes sur la Maintenance"
          subtitle="Découvrez comment nous assurons la pérennité et la performance de vos applications"
          items={faqItems}
          className="bg-slate-50"
        />
      </div>
    </>
  );
};

export default MaintenanceSupport; 